import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import RacingOfficialPCPageState from "interfaces/RacingOfficialPCPageState";
import {
  hasChangedSavedValues,
  hasSelectedFilters,
  NUMBER,
  PRIMITIVES_ARRAY
} from "components/Filters/helper";
import {
  initialDaysSinceLastRace,
  surfaceTypes,
  genderTypes,
  MIN_VALUE,
  MAX_VALUE
} from "./PreferredConditionsFilters/filtersOptions";

export interface IStatePreferredConditionFilters {
  daysSinceLastRace: number[];
  setDaysSinceLastRace: (value: number[]) => void;
  selectedSurface: number;
  setSelectedSurface: (value: number) => void;
  selectedGender: number;
  setSelectedGender: (value: number) => void;
  selectedDistances: string[];
  setSelectedDistances: (value: string[]) => void;
  selectedRaceTypes: string[];
  setSelectedRaceTypes: (value: string[]) => void;
  selectedClaimingPrices: string[];
  setSelectedClaimingPrices: (value: string[]) => void;
  selectedAge: string[];
  setSelectedAge: (value: string[]) => void;
  hasChangedFilterValue: boolean;
  hasSavedFilterValue: boolean;
  hasChangedSavedFiltersValues: boolean;
}

export const useStatePreferredConditionFilters =
  (): IStatePreferredConditionFilters => {
    const { filters: reduxFilters } = useSelector(
      (state: { racingOfficialPCPage: RacingOfficialPCPageState }) => {
        return state.racingOfficialPCPage;
      }
    );

    const [daysSinceLastRace, setDaysSinceLastRace] = useState<number[]>(
      initialDaysSinceLastRace
    );
    const [selectedSurface, setSelectedSurface] = useState<number>(
      surfaceTypes[0].value
    );
    const [selectedGender, setSelectedGender] = useState<number>(
      genderTypes[0].value
    );
    const [selectedDistances, setSelectedDistances] = useState<string[]>(null);
    const [selectedRaceTypes, setSelectedRaceTypes] = useState<string[]>(null);
    const [selectedClaimingPrices, setSelectedClaimingPrices] =
      useState<string[]>(null);
    const [selectedAge, setSelectedAge] = useState<string[]>(null);

    const localeStateFilters = {
      daysSinceLastRace,
      selectedSurface,
      selectedGender,
      selectedDistances,
      selectedRaceTypes,
      selectedClaimingPrices,
      selectedAge
    };

    const hasChangedFilterValue = hasSelectedFilters(
      Object.values(localeStateFilters),
      MIN_VALUE,
      MAX_VALUE
    );
    const hasSavedFilterValue = Boolean(
      reduxFilters &&
        hasSelectedFilters(Object.values(reduxFilters), MIN_VALUE, MAX_VALUE)
    );
    const hasChangedSavedFiltersValues = [
      hasChangedSavedValues(
        daysSinceLastRace,
        reduxFilters?.daysSinceLastRace,
        PRIMITIVES_ARRAY
      ),
      hasChangedSavedValues(
        selectedSurface,
        reduxFilters?.selectedSurface,
        NUMBER
      ),
      hasChangedSavedValues(
        selectedGender,
        reduxFilters?.selectedGender,
        NUMBER
      ),
      hasChangedSavedValues(
        selectedDistances,
        reduxFilters?.selectedDistances,
        PRIMITIVES_ARRAY
      ),
      hasChangedSavedValues(
        selectedRaceTypes,
        reduxFilters?.selectedRaceTypes,
        PRIMITIVES_ARRAY
      ),
      hasChangedSavedValues(
        selectedClaimingPrices,
        reduxFilters?.selectedClaimingPrices,
        PRIMITIVES_ARRAY
      ),
      hasChangedSavedValues(
        selectedAge,
        reduxFilters?.selectedAge,
        PRIMITIVES_ARRAY
      )
    ].some(item => item);

    useEffect(() => {
      setDaysSinceLastRace(
        reduxFilters?.daysSinceLastRace || initialDaysSinceLastRace
      );
      setSelectedSurface(
        reduxFilters?.selectedSurface || surfaceTypes[0].value
      );
      setSelectedGender(reduxFilters?.selectedGender || genderTypes[0].value);
      setSelectedDistances(reduxFilters?.selectedDistances || null);
      setSelectedRaceTypes(reduxFilters?.selectedRaceTypes || null);
      setSelectedClaimingPrices(reduxFilters?.selectedClaimingPrices || null);
      setSelectedAge(reduxFilters?.selectedAge || null);
    }, [reduxFilters]);

    return {
      daysSinceLastRace,
      setDaysSinceLastRace,
      selectedSurface,
      setSelectedSurface,
      selectedDistances,
      setSelectedDistances,
      selectedRaceTypes,
      setSelectedRaceTypes,
      selectedClaimingPrices,
      setSelectedClaimingPrices,
      selectedGender,
      setSelectedGender,
      selectedAge,
      setSelectedAge,
      hasChangedFilterValue,
      hasSavedFilterValue,
      hasChangedSavedFiltersValues
    };
  };
